import type { Card, CardStatus } from '@/integrations/Immersve/cards/types';

const STATUS_TONES = {
  green: 'bg-green-50 text-green-700 ring-green-600/20',
  gray: 'bg-gray-50 text-gray-600 ring-gray-500/20',
  lightPink: 'bg-pink-50/60 text-pink-600 ring-pink-500/20',
  blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
};

// Expired is not an amethyst status: it is shown in place of one once the
// card is past its expiry date, or was cancelled for that reason.
export type CardDisplayStatus = CardStatus | 'expired';

// creating, activating and cancelling are transitions still in flight and
// share blue, as card tokens' do.
export const STATUS_TONE_CLASSES: Record<CardDisplayStatus, string> = {
  active: STATUS_TONES.green,
  created: STATUS_TONES.indigo,
  shipped: STATUS_TONES.indigo,
  cancelled: STATUS_TONES.lightPink,
  expired: STATUS_TONES.gray,
  creating: STATUS_TONES.blue,
  activating: STATUS_TONES.blue,
  cancelling: STATUS_TONES.blue,
};

// A card cancelled for any other reason stays cancelled once it also passes
// its expiry date: the cancellation is the more telling of the two.
export function displayStatus(card: Card, now: Date = new Date()): CardDisplayStatus {
  if (card.status === 'cancelled') {
    return card.cancelReason === 'expired' ? 'expired' : 'cancelled';
  }
  return new Date(card.expiresAt) <= now ? 'expired' : card.status;
}

// Frozen sits beside the status rather than replacing it: a blocked card is
// still active, cancelled, and so on.
export const BLOCKED_TONE_CLASSES = 'bg-orange-50 text-orange-700 ring-orange-600/20';

export function maskedPan(card: Card): string {
  if (!card.panFirst6 || !card.panLast4) {
    return card.panLast4 ? `•••• ${card.panLast4}` : '—';
  }
  return `${card.panFirst6.slice(0, 4)} ${card.panFirst6.slice(4)}•• •••• ${card.panLast4}`;
}

/** 202910 → 10/29. */
export function expiryLabel(card: Card): string {
  const match = card.expiry?.match(/^\d{2}(\d{2})(\d{2})$/);
  return match ? `${match[2]}/${match[1]}` : '—';
}

export function dashedLabel(value: string | undefined): string {
  return value ? value.replace(/-/g, ' ') : '—';
}

// Amethyst falls back to the card's own ID for legacy cards without one.
export function seriesOf(card: Card): string {
  return card.seriesId ?? card.id;
}
